import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useSnapCarousel } from '../shared/useSnapCarousel'
import { InstructorCard } from './InstructorCard'
import type { InstructorItem } from './types'

export interface InstructorsCarouselHandle {
  goToPrev: () => void
  goToNext: () => void
}

export interface InstructorsCarouselNavState {
  canGoPrev: boolean
  canGoNext: boolean
}

interface InstructorsCarouselProps {
  items: InstructorItem[]
  sectionLabel: string
  id?: string
  onNavStateChange?: (navState: InstructorsCarouselNavState) => void
}

const SLIDE_CLASS_NAME = [
  'shrink-0 snap-start transition-opacity duration-500 ease-in-out',
  'md:data-[clipped=true]:opacity-20',
].join(' ')

/** Upper bound on a browser's smooth-scroll; only used to release the guard. */
const SCROLL_ANIMATION_MS = 600

export const InstructorsCarousel = forwardRef<
  InstructorsCarouselHandle,
  InstructorsCarouselProps
>(({ items, sectionLabel, id, onNavStateChange }, ref) => {
  const activeIndexRef = useRef(0)
  // Null until the first sync so the initial state is always emitted, even
  // when it happens to equal the parent's seed. Without that, a rail that
  // does not overflow never corrects an optimistic `canGoNext`.
  const navStateRef = useRef<InstructorsCarouselNavState | null>(null)
  const onNavStateChangeRef = useRef(onNavStateChange)
  onNavStateChangeRef.current = onNavStateChange

  const isAnimatingRef = useRef(false)
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [clipped, setClipped] = useState<boolean[]>([])
  const clippedRef = useRef<boolean[]>([])

  const syncState = useCallback((track: HTMLDivElement) => {
    const trackRect = track.getBoundingClientRect()
    const slides = Array.from(track.children) as HTMLElement[]

    const nextClipped = slides.map((slide) => {
      const slideRect = slide.getBoundingClientRect()
      return (
        slideRect.left < trackRect.left - 1 ||
        slideRect.right > trackRect.right + 1
      )
    })

    if (
      nextClipped.length !== clippedRef.current.length ||
      nextClipped.some((value, index) => value !== clippedRef.current[index])
    ) {
      clippedRef.current = nextClipped
      setClipped(nextClipped)
    }

    // While our own smooth scroll is in flight the closest slide is one we are
    // passing, not the destination — adopting it would make a fast double-click
    // of an arrow advance one card instead of two.
    if (!isAnimatingRef.current) {
      let closestIndex = 0
      let closestDistance = Infinity
      slides.forEach((slide, index) => {
        const distance = Math.abs(
          slide.getBoundingClientRect().left - trackRect.left,
        )
        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      })
      activeIndexRef.current = closestIndex
    }

    const nextNavState: InstructorsCarouselNavState = {
      canGoPrev: track.scrollLeft > 1,
      canGoNext: track.scrollLeft + track.clientWidth < track.scrollWidth - 1,
    }
    if (
      !navStateRef.current ||
      nextNavState.canGoPrev !== navStateRef.current.canGoPrev ||
      nextNavState.canGoNext !== navStateRef.current.canGoNext
    ) {
      navStateRef.current = nextNavState
      onNavStateChangeRef.current?.(nextNavState)
    }
  }, [])

  const { scrollRef } = useSnapCarousel({ sync: syncState, resize: 'observer' })

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = scrollRef.current
      if (!track) return

      const slide = track.children[index] as HTMLElement | undefined
      if (!slide) return

      activeIndexRef.current = index
      isAnimatingRef.current = true
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current)
      animationTimerRef.current = setTimeout(() => {
        isAnimatingRef.current = false
        animationTimerRef.current = null
      }, SCROLL_ANIMATION_MS)

      const left =
        slide.getBoundingClientRect().left -
        track.getBoundingClientRect().left +
        track.scrollLeft
      track.scrollTo({ left })
    },
    [scrollRef],
  )

  const goToPrev = useCallback(() => {
    scrollToIndex(Math.max(0, activeIndexRef.current - 1))
  }, [scrollToIndex])

  const goToNext = useCallback(() => {
    scrollToIndex(Math.min(items.length - 1, activeIndexRef.current + 1))
  }, [scrollToIndex, items.length])

  useImperativeHandle(ref, () => ({ goToPrev, goToNext }), [goToPrev, goToNext])

  // Clear the animation-guard timer on unmount.
  useEffect(
    () => () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current)
    },
    [],
  )

  // Re-measure when the slide count changes (the hook only syncs on mount).
  useEffect(() => {
    const track = scrollRef.current
    if (track) syncState(track)
  }, [scrollRef, syncState, items.length])

  return (
    <div
      ref={scrollRef}
      id={id}
      role='region'
      aria-roledescription='carousel'
      aria-label={sectionLabel}
      className='flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth [scrollbar-width:none] motion-reduce:scroll-auto md:gap-4 [&::-webkit-scrollbar]:hidden'
    >
      {items.map((instructor, index) => (
        <div
          key={instructor.id}
          role='group'
          aria-roledescription='slide'
          aria-label={`${index + 1} of ${items.length}`}
          data-clipped={clipped[index] === true}
          className={SLIDE_CLASS_NAME}
        >
          <InstructorCard instructor={instructor} />
        </div>
      ))}
    </div>
  )
})

InstructorsCarousel.displayName = 'InstructorsCarousel'
